export default {
  hero: {
    title: "Sell your surplus solar energy to local consumers",
    description:
      "Earn more than feed-in tariffs by selling directly to neighbors within your community. We handle the complexity you receive the payments.",
    cta: "Start selling",
    "bullet-1": "Higher revenue than feed-in tariffs",
    "bullet-2": "No complex administration",
    "bullet-3": "Regular monthly payments",
  },
  "why-sell": {
    title: "Why sell through community solar?",
    subtitle:
      "This is a business decision, not ideology. See why producers choose local energy trading.",
    cards: {
      "higher-revenue": {
        title: "Higher revenue",
        description:
          "Sell at local market prices instead of regulated feed-in tariffs. Earn up to 30% more per kWh.",
      },
      "predictable-income": {
        title: "Predictable income",
        description:
          "Manage permissions efficiently to streamline team workflows and enhance collaboration.",
      },
      "local-value": {
        title: "Local value",
        description:
          "Streamline processes and enhance efficiency with intelligent systems.",
      },
    },
  },
  "how-it-works": {
    title: "How it works",
    subtitle: "From production to payment, a smooth journey managed for you.",
    steps: {
      "step-1": {
        title: "Your installation produces",
        description:
          "Your solar panels generate electricity as usual. Surplus energy you don't consume can be shared.",
      },
      "step-2": {
        title: "Energy allocated locally",
        description:
          "Your surplus energy is allocated to nearby consumers within a 2 to 20 km radius, thanks to Enedis' regulated framework.",
      },
      "step-3": {
        title: "We handle everything",
        description:
          "ProjetSolaire manages PMO setup, legal entities, repartition keys, Enedis API coordination, contracts, and compliance.",
      },
      "step-4": {
        title: "You receive payments",
        description:
          "Receive monthly payments based on the kWh actually delivered.",
      },
    },
  },
  "need-consumers": {
    "title-before": "Need",
    "title-highlight": "consumers",
    "title-after": "? We help you find them",
    description:
      "Don't worry about finding local buyers for your surplus energy. We support recruitment—your installer activates local prospecting to build your community.",
    benefits: {
      "local-targeting": "Local targeting within your eligible zone",
      "qualified-leads": "Qualified leads from interested households",
      "community-support": "Community building support and materials",
    },
    cta: "Start Building Your Community",
    stats: {
      "consumers-in-network": "Consumers in Network",
      "successful-matches": "Successful Matches",
      "avg-time-to-full": "Avg. Time to Full",
      "partner-installers": "Partner Installers",
    },
  },
  eligibility: {
    "title-before": "Are you",
    "title-highlight": "eligible",
    "title-after": "?",
    subtitle:
      "Quickly check if your installation qualifies for community solar energy trading.",
    "most-qualify": "Most solar installations qualify",
    paragraph:
      "If you have a working solar installation with a Linky meter and potential consumers nearby, you're likely eligible to start earning more from your surplus energy.",
    cta: "Check my eligibility",
    criteria: {
      technical: {
        title: "Technical",
        items: {
          enedis: "Active Enedis connection (MES approved)",
          linky: "Linky smart meter installed",
          installation: "Solar installation in operation",
        },
      },
      geographic: {
        title: "Geographic",
        items: {
          urban: "Consumers within 2 km (urban areas)",
          rural: "Up to 20 km for rural installations",
          zone: "Same distribution network zone",
        },
      },
      contractual: {
        title: "Contractual",
        items: {
          injection: "Partial or total surplus injection",
          contracts: "No exclusive long-term contracts",
          willingness: "Willingness to participate in community",
        },
      },
    },
  },
  monitor: {
    title: "Monitor your community",
    subtitle:
      "Stay in control with real-time insights into your energy community's performance.",
    "image-alt": "Dashboard showing energy community metrics, charts and consumer list",
  },
  "calculate-earnings": {
    title: "Calculate your potential earnings",
    subtitle:
      "See how much more you could earn by selling to your community instead of feed-in tariffs.",
    "installation-size": "Installation size",
    "surplus-energy": "Surplus energy",
    "feed-in-rate": "Current feed-in rate",
    "additional-revenue": "Additional annual revenue",
    "more-than-feed-in": "30% more than feed-in tariffs",
    "feed-in-tariff": "Feed-in tariff",
    "community-solar": "Community solar",
    "surplus-kwh": "Surplus energy",
    "info-note":
      "Indicative projections based on your inputs and local consumption demand.",
    cta: "Get personalized Quote",
    "slider-min": "min",
    "slider-max": "max",
  },
  pricing: {
    title: "Transparent, performance-aligned pricing",
    subtitle:
      "Simple, percentage-based pricing aligned with your project scale.",
    "plan-title": "Producer plan",
    "plan-subtitle": "Everything you need to sell locally",
    "what-you-get": "What you get",
    "pricing-rows": {
      "setup-fee": { label: "Setup fee", subtitle: "One-time, pretax" },
      "monthly-management": {
        label: "Monthly management",
        subtitle: "Billed monthly",
      },
      "transaction-fee": { label: "Transaction fee", subtitle: "Of kWh price" },
    },
    features: {
      pmo: "Full PMO management",
      "consumer-acquisition": "Consumer acquisition",
      "enedis-api": "Enedis API coordination",
      billing: "Billing & invoicing",
    },
    comparison: {
      "buying-column": "Buying solar",
      "leasing-column": "Solar leasing",
      "upfront-cost": { criteria: "Upfront cost", buying: "High", leasing: "None" },
      "monthly-cost": {
        criteria: "Monthly cost",
        buying: "None",
        leasing: "Fixed monthly fee",
      },
      maintenance: {
        criteria: "Maintenance",
        buying: "Your cost",
        leasing: "Included",
      },
      warranty: {
        criteria: "Warranty",
        buying: "Limited",
        leasing: "Full coverage",
      },
      flexibility: {
        criteria: "Flexibility",
        buying: "Low",
        leasing: "Buy after 10 years",
      },
    },
  },
  "we-handle-complexity": {
    title: "We handle the complexity",
    subtitle:
      "Everything that would be painful without us managed seamlessly.",
    cards: {
      "ongoing-support": {
        title: "Ongoing support",
        description:
          "Dedicated support team to help you and your consumers with any questions or issues.",
      },
      "legal-pmo": {
        title: "Legal entity and PMO management",
        description:
          "We handle the creation and management of your energy community's legal structure.",
      },
      "enedis-coordination": {
        title: "Enedis coordination",
        description:
          "All grid operator communications, repartition keys, and technical requirements managed for you.",
      },
      regulatory: {
        title: "Regulatory compliance",
        description:
          "Stay compliant with all energy regulations and reporting requirements automatically.",
      },
      billing: {
        title: "Billing and invoicing",
        description:
          "Automated billing cycles, payment collection, and transparent revenue distribution.",
      },
      "consumer-onboarding": {
        title: "Consumer onboarding",
        description:
          "We recruit, onboard, and manage consent from local consumers in your community.",
      },
    },
  },
  cta: {
    title: "Your solar energy is worth more than a regulated tariff",
    subtitle:
      "Join hundreds of producers earning up to 3x more by selling directly to local consumers",
    "no-credit-check": "No credit check",
    "quick-process": "Quick process",
    "no-obligation": "No obligation",
    button: "Find a community near me",
  },
}
